import { MapDiscriminatedUnion, Optional } from '@app/core/utils/types';
import { ValidationErrors } from '../core/utils/validators';
import { FirstMileType } from '../ecsomag/models/order';
import { Icon } from './blocks';
import { LastMileType } from '../ecsomag/models';

export interface ShippingInfo {
	zones: ShippingZones;
	countries: ShippingCountries;
	pricingTypes: PricingTypes;
	packageSizes: PackageSizes;
	extraServices: ExtraServices;
	constraints: Record<FirstMileType, ParcelConstraints>;
	multipleDiscounts: 'sum' | 'largest';
	parcelDiscounts: ParcelDiscounts;
}

export type ShippingZoneId = 0 | 1 | 2 | 3 | 4;
export type ShippingZoneType = 'domestic' | 'international';
export interface ShippingZone {
	id: ShippingZoneId;
	name: string;
	type: ShippingZoneType;
}
export type ShippingZones = Array<ShippingZone>;

export type ShippingCountryId = number;
export interface ShippingCountry {
	id: ShippingCountryId;
	name: string;
	isoCode: string;
	zoneId: ShippingZoneId;
	leadTime: string;
	weightLimit: number;
	lastMileTypes: LastMileType[];
	requireFinalDeliveryAddress: boolean;
}
export type ShippingCountries = Array<ShippingCountry>;

export type UserType = 'guest' | 'registered';
export const FALLBACK_PRICING_TYPE_ID = '*:*:*';
export type PricingTypeId = typeof FALLBACK_PRICING_TYPE_ID | `${FirstMileType | '*'}:${LastMileType | '*'}:${UserType | '*'}`;
export interface PricingType {
	id: PricingTypeId;
	name: string;
	description: string;
	firstMileType: FirstMileType | '*';
	lastMileType: LastMileType | '*';
	userType: UserType | '*';
}
export type PricingTypes = Array<PricingType>;

export type PackageSizeName = 'XS' | 'S' | 'M' | 'L' | 'XL';
export type PackageSizeLimitType = 'two_sides' | 'circumference';
export type ShippingPriceVariants = Record<typeof FALLBACK_PRICING_TYPE_ID, number> & Partial<Record<PricingTypeId, number>>;
export type ShippingPricePerZone = [zone: ShippingZoneId, price: ShippingPriceVariants];
export interface PackageSize {
	size: PackageSizeName;
	sizeLimitType: PackageSizeLimitType;
	sizeLimit: number;
	order: number;
	description: string;
	prices: ShippingPricePerZone[];
}
export type PackageSizes = Array<PackageSize>;

export const parcelDimensionKeys = ['length', 'width', 'height'] as const;
export type ParcelDimensionKey = typeof parcelDimensionKeys[number];
export type MinMaxConstraint = {
	min: number;
	max: number;
};
export type ParcelConstraints = {
	dimension: Record<ParcelDimensionKey, MinMaxConstraint>;
	circumference: MinMaxConstraint;
};

export interface TieredPrice {
	field: ExtraServicesAdditionalDataPath;
	min: number;
	max: number;
	price: number;
	name: string;
}

export type ExtraServiceId = number;

// extra service additional data

export interface ExtraServiceAdditionalDataCOD {
	type: 'cod';
	amount: number;
	bankAccountHolder: string;
	bankAccountNumber: string;
}

export interface ExtraServiceAdditionalDataCODAmount {
	type: 'cod-amount';
	amount: number;
}

export interface ExtraServiceAdditionalDataCODBankAccount {
	type: 'cod-bankAccount';
	bankAccountHolder: string;
	bankAccountNumber: string;
}

export type ExtraServiceAdditionalData = ExtraServiceAdditionalDataCOD | ExtraServiceAdditionalDataCODAmount | ExtraServiceAdditionalDataCODBankAccount;

export type ExtraServicesAdditionalDataKey = ExtraServiceAdditionalData['type'];
export type ExtraServicesAdditionalDataMap = MapDiscriminatedUnion<ExtraServiceAdditionalData, 'type'>;

export const extraServicesAdditionalDataKeys: readonly ExtraServicesAdditionalDataKey[] = ['cod', 'cod-amount', 'cod-bankAccount'];

export const isExtraServicesAdditionalDataKey = (k: string): k is ExtraServicesAdditionalDataKey => (extraServicesAdditionalDataKeys as string[]).includes(k);

// also possible:
// type T_by_type = DiscriminateUnion<ExtraServiceAdditionalData, 'type', 'cod-amount'>;

export type ExtraServicesAdditionalDataPath =
	| `${ExtraServiceAdditionalDataCOD['type']}.${keyof ExtraServiceAdditionalDataCOD}`
	| `${ExtraServiceAdditionalDataCODAmount['type']}.${keyof ExtraServiceAdditionalDataCODAmount}`
	| `${ExtraServiceAdditionalDataCODBankAccount['type']}.${keyof ExtraServiceAdditionalDataCODBankAccount}`;

export type ExtraServicePartialAdditionalDataCOD = Optional<ExtraServiceAdditionalDataCOD, 'amount' | 'bankAccountNumber' | 'bankAccountHolder'>;
export type ExtraServicePartialAdditionalDataCODAmount = Optional<ExtraServiceAdditionalDataCODAmount, 'amount'>;
export type ExtraServicePartialAdditionalDataCODBankAccount = Optional<ExtraServiceAdditionalDataCODBankAccount, 'bankAccountNumber' | 'bankAccountHolder'>;

export type ExtraServicePartialAdditionalData = ExtraServicePartialAdditionalDataCOD | ExtraServicePartialAdditionalDataCODAmount | ExtraServicePartialAdditionalDataCODBankAccount;

export type ExtraServicesPartialAdditionalDataKey = ExtraServicePartialAdditionalData['type'];
export type ExtraServicesPartialAdditionalDataMap = MapDiscriminatedUnion<ExtraServicePartialAdditionalData, 'type'>;

// extra service base
export type ExtraServiceBase = {
	id: ExtraServiceId;
	key: string;

	countries: ShippingCountryId[];

	shortName: string;
	longName: string;
	shortDescription: string;
	longDescription: string;
	importantInfo?: string;
	additionalInfos: {
		title: string;
		description: string;
	}[];

	destinationTypes?: LastMileType[];
	additionalData?: ExtraServicesAdditionalDataKey[];
	requiredFields?: string[];
	relatedFields?: string[];
	mutuallyExclusiveServices?: ExtraServiceId[];

	selectablePerParcel: boolean;
	selectablePerOrder: boolean;
};

export type ExtraServiceCalculatedProps = {
	available: boolean;
	selected: boolean;
	additionalDataValues?: Partial<ExtraServicesPartialAdditionalDataMap>;
	effectivePrice?: number;
	// valid?: boolean,
	// errors?: Error[],
	// validForCalculation?: boolean,
	// errorsForCalculation?: Error[],
	validity?: {
		calculator: {
			errors: Record<string, ValidationErrors>;
			valid: boolean;
		};
		dispatch: {
			errors: Record<string, ValidationErrors>;
			valid: boolean;
		};
	};
};

// extra service price
export interface ExtraServicePriceSimple {
	priceType: 'simple';
	price: number;
}
export interface ExtraServicePriceTiered {
	priceType: 'tiered';
	price: TieredPrice[];
}
export type ExtraServicePrice = ExtraServicePriceSimple | ExtraServicePriceTiered;

// final extra service type
export type ExtraService = ExtraServiceBase & ExtraServicePrice;

export interface ExtraServicesGroup {
	title: string;
	subtitle: string;
	icon: Icon;
	ids: ExtraServiceId[];
}

export interface MutuallyExclusiveServices {
	name: string;
	ids: ExtraServiceId[];
}

export interface ExtraServices {
	services: ExtraService[];
	groups: ExtraServicesGroup[];
	// mutuallyExclusiveServices: MutuallyExclusiveServices[],
}

// parcel discounts
export interface ParcelDiscount {
	id: number;
	name: string;
	description: string;
	condition: string | object;
	conditions: {
		authRequired?: boolean;
	};
	operator: 'add' | 'multiply';
	amount: number;
	discountBase: 'total' | 'base';
	active: boolean;
}
export type ParcelDiscounts = Array<ParcelDiscount>;

// API version
export type ExtraServiceBaseAPI = Optional<ExtraService, 'additionalData'>;
export type ExtraServiceAPI = ExtraServiceBaseAPI & ExtraServicePrice;

// export type ExtraServicesGroupAPI = Pick<ExtraServicesGroup, 'title' | 'subtitle' | 'icon'> & {
// 	extraServices: ExtraServiceId[];
// }

export type ShippingPricesAPI = Pick<ShippingInfo, 'zones' | 'countries' | 'pricingTypes' | 'packageSizes' | 'multipleDiscounts' | 'parcelDiscounts'> & {
	extraServices: {
		services: ExtraServiceAPI[];
		groups: ExtraServicesGroup[];
		mutuallyExclusiveServices: MutuallyExclusiveServices[];
	};
};
